import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import RoomVisualizer from "./components/RoomVisualizer"
import Sidebar from "./components/Sidebar";
import RetailerSignup from "./components/retailer/RetailerSignup";
import RetailerLogin from "./components/retailer/RetailerLogin";
import RetailerDashboard from "./components/retailer/RetailerDashboard";
import AddWallpaper from "./components/retailer/AddWallpaper";
import ProductSizes from "./components/retailer/ProductSizes";
import Tiles from "./components/retailer/Tiles";
import Wallpapers from "./components/retailer/Wallpapers";
import RetailerVisualizerRoute from "./components/retailer/RetailerVisualizerRoute";
import ImageSettings from "./components/retailer/ImageSettings";
import AdminLogin from "./components/admin/AdminLogin";
import AdminDashboard from "./components/admin/AdminDashboard";
import AdminRetailers from "./components/admin/AdminRetailers";
import AdminRetailerActivity from "./components/admin/AdminRetailerActivity";
import { generateMask, getDemoRoom } from "./services/api";
import { DEFAULT_REGION_SETTINGS } from "./data/regionSettings";
import BulkProductUpload from "./pages/BulkProductUpload"
import DigitalShowroom from "./pages/DigitalShowroom";
import ProductDetail from "./components/DigitalShowroom/ProductDetail";
import room1 from "../src/assets/rooms/room1.jpg";
import room2 from "../src/assets/rooms/room2.jpg";
import room3 from "../src/assets/rooms/room3.jpg";
import room4 from "../src/assets/rooms/room4.jpg";
import ProductInquiriesPage from "./pages/ProductInquiry";
import PreviewSettingPage from "./pages/PreviewSettingPage";
import CatalogSettingsPage from "./pages/CatalogSettingsPage";
import { RetailerAuthProvider, RetailerProtectedRoute } from './components/retailer/RetailerAuthContext'; // adjust the path to wherever you saved it
import { Toaster } from "react-hot-toast";
import OrderHistoryPage from "./pages/OrderHistoryPage";
import SelectionPage from "./components/retailer/selection/SelectionPage";
import QuotationPreviewPage from "./pages/QuotationPreviewPage";


// Demo room card component
function DemoRoomCard({ image, name, roomId, onDemoSelect }) {
  const handleDemoClick = async () => {
    try {
      const response = await getDemoRoom(roomId);
      if (response.success) {
        onDemoSelect(response);
      } else {
        console.error('Failed to load demo room:', response.error);
      }
    } catch (err) {
      console.error('Failed to load demo room:', err);
    }
  };

  return (
    <button
      onClick={handleDemoClick}
      className="group relative rounded-xl overflow-hidden border border-gray-200 hover:border-accent transition-all hover:shadow-lg"
    >
      <img
        src={image}
        alt={name}
        className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
        <span className="text-white text-sm font-medium">{name}</span>
      </div>
    </button>
  );
}

const CM_TO_FT = 1 / 30.48;

function getMaterialDimensionSettings(material) {
  if (!["tile", "wallpaper"].includes(material?.type)) return {};

  return {
    ...(typeof material.width_cm === "number"
      ? { tileWidth: material.width_cm * CM_TO_FT }
      : {}),
    ...(typeof material.height_cm === "number"
      ? { tileHeight: material.height_cm * CM_TO_FT }
      : {}),
  };
}

function VisualizerRoute() {
  const [data, setData] = useState(null);
  const [selectedRegions, setSelectedRegions] = useState({});
  const [regionMaterials, setRegionMaterials] = useState({});
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [draftSettings, setDraftSettings] = useState(DEFAULT_REGION_SETTINGS);
  const [loading, setLoading] = useState(false);

  const selectedRegion = Object.keys(selectedRegions).find(
    (region) => selectedRegions[region],
  );

  useEffect(() => {
    setRegionMaterials({});
    setSelectedRegions({});
  }, [data?.project_id, data?.image_path]);

  useEffect(() => {
    if (!selectedRegion) {
      setSelectedMaterial(null);
      setDraftSettings(DEFAULT_REGION_SETTINGS);
      return;
    }

    const applied = regionMaterials[selectedRegion];
    if (applied) {
      setSelectedMaterial({
        type: applied.type,
        value: applied.value,
        width_cm: applied.width_cm,
        height_cm: applied.height_cm,
      });
      setDraftSettings(applied.settings ?? DEFAULT_REGION_SETTINGS);
      return;
    }

    setSelectedMaterial(null);
    setDraftSettings(DEFAULT_REGION_SETTINGS);
  }, [selectedRegion, regionMaterials]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    try {
      const response = await generateMask(file);
      console.log("Response: ", response)
      setData(response);
    } catch (err) {
      console.error(err);
      alert("Failed to process room image");
    } finally {
      setLoading(false);
      event.target.value = "";
    }
  };

  const handleDemoUpload = async (event) => {
    await handleUpload(event);
  };

  const handleDemoSelect = async (demoData) => {
    setLoading(true);
    try {
      setData(demoData);
    } catch (err) {
      console.error(err);
      alert("Failed to load demo room");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMaterial = (material) => {
    setSelectedMaterial(material);

    const materialDimensionSettings = getMaterialDimensionSettings(material);
    if (Object.keys(materialDimensionSettings).length > 0) {
      setDraftSettings((prev) => ({
        ...prev,
        ...materialDimensionSettings,
      }));
    }
  };

  const handleDirectApply = (material) => {
    if (!selectedRegion) return;

    const settingsToSave = { ...draftSettings };
    Object.assign(settingsToSave, getMaterialDimensionSettings(material));

    setRegionMaterials((prev) => ({
      ...prev,
      [selectedRegion]: {
        ...material,
        settings: settingsToSave,
      },
    }));
  };

  const handleGroutChange = (key, value) => {
    if (!selectedRegion || !regionMaterials[selectedRegion]) return;

    setRegionMaterials((prev) => ({
      ...prev,
      [selectedRegion]: {
        ...prev[selectedRegion],
        settings: {
          ...prev[selectedRegion].settings,
          [key]: value,
        },
      },
    }));
  };

  const handleRemoveGrout = () => {
    if (!selectedRegion || !regionMaterials[selectedRegion]) return;

    setRegionMaterials((prev) => ({
      ...prev,
      [selectedRegion]: {
        ...prev[selectedRegion],
        settings: {
          ...prev[selectedRegion].settings,
          groutWidth: 0,
          groutColor: DEFAULT_REGION_SETTINGS.groutColor,
        },
      },
    }));
    setDraftSettings((prev) => ({
      ...prev,
      groutWidth: 0,
      groutColor: DEFAULT_REGION_SETTINGS.groutColor,
    }));
  };

  const applyMaterial = () => {
    if (!selectedRegion || !selectedMaterial) return;

    const settingsToSave = { ...draftSettings };
    Object.assign(settingsToSave, getMaterialDimensionSettings(selectedMaterial));

    setRegionMaterials((prev) => ({
      ...prev,
      [selectedRegion]: {
        ...selectedMaterial,
        settings: settingsToSave,
      },
    }));
  };

  const handleClearMaterials = () => {
    setRegionMaterials({});
    setSelectedRegions({});
    setSelectedMaterial(null);
    setDraftSettings(DEFAULT_REGION_SETTINGS);
  };

  if (data) {
    return (
      <div className="w-screen h-screen bg-white flex overflow-hidden">
        <Sidebar
          onSelect={handleSelectMaterial}
          settings={draftSettings}
          setSettings={setDraftSettings}
          onApply={applyMaterial}
          selectedRegion={selectedRegion}
          selectedMaterial={selectedMaterial}
          regionMaterials={regionMaterials}
          onClearMaterials={handleClearMaterials}
          onDirectApply={handleDirectApply}
          onGroutChange={handleGroutChange}
          onRemoveGrout={handleRemoveGrout}
        />

        <div className="flex-1 min-w-0 h-full relative flex items-center justify-center overflow-hidden">
          <button
            onClick={() => setData(null)}
            className="absolute top-6 left-6 z-50 px-5 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Back
          </button>

          <RoomVisualizer
            key={data.project_id || data.image_path || "room-visualizer"}
            data={data}
            selectedRegions={selectedRegions}
            setSelectedRegions={setSelectedRegions}
            regionMaterials={regionMaterials}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-5xl">
        <div className="rounded-3xl bg-white p-12 shadow-xl border border-gray-200 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            AI Room Visualizer
          </h1>

          <p className="text-gray-600 mb-8">
            Upload your room image to start visualizing materials, or try a demo room.
          </p>

          <label className="cursor-pointer block">
            <div className="px-8 py-4 bg-accent hover:bg-accent2 rounded-xl text-white font-medium inline-block transition-colors">
              Choose Room Image
            </div>

            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
          </label>

          {loading && (
            <div className="mt-8 text-gray-600">
              Processing room geometry...
            </div>
          )}

          {/* Demo Rooms Section */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Try Demo Rooms</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <DemoRoomCard image={room1} name="Modern Living Room" roomId={1} onDemoSelect={handleDemoSelect} />
              <DemoRoomCard image={room2} name="Cozy Bedroom" roomId={2} onDemoSelect={handleDemoSelect} />
              <DemoRoomCard image={room3} name="Spacious Kitchen" roomId={3} onDemoSelect={handleDemoSelect} />
              <DemoRoomCard image={room4} name="Elegant Dining" roomId={4} onDemoSelect={handleDemoSelect} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <RetailerAuthProvider>
        <Toaster
          position="top-right"
          reverseOrder={false}
          toastOptions={{
            duration: 3000,
          }}
        />

        <div className="min-h-screen font-body text-t1">
          <Routes>
            <Route path="/" element={<><Navbar /><Home /></>} />
            <Route path="/signin" element={<><Navbar /><SignIn /></>} />
            <Route path="/signup" element={<><Navbar /><SignUp /></>} />
 
            {/* Public retailer-facing pages: no guard */}
            <Route path="/retailer/signup" element={<RetailerSignup />} />
            <Route path="/retailer/login" element={<RetailerLogin />} />
 
            {/* Everything below requires a logged-in retailer.
                Same wrapping pattern for each one — copy it for any
                retailer route not listed here too. */}
            <Route path="/retailer/dashboard" element={
              <RetailerProtectedRoute><RetailerDashboard /></RetailerProtectedRoute>
            } />
            <Route path="/retailer/tiles" element={
              <RetailerProtectedRoute><Tiles /></RetailerProtectedRoute>
            } />
            <Route path="/retailer/wallpapers" element={
              <RetailerProtectedRoute><Wallpapers /></RetailerProtectedRoute>
            } />
            <Route path="/retailer/add-wallpaper" element={
              <RetailerProtectedRoute><AddWallpaper /></RetailerProtectedRoute>
            } />
            <Route path="/retailer/product-sizes" element={
              <RetailerProtectedRoute><ProductSizes /></RetailerProtectedRoute>
            } />
            <Route path="/retailer/product-inquiries" element={
              <RetailerProtectedRoute><ProductInquiriesPage /></RetailerProtectedRoute>
            } />
            <Route path="/retailer/order-history" element={
              <RetailerProtectedRoute><OrderHistoryPage /></RetailerProtectedRoute>
            } />
            <Route path="/retailer/image-settings" element={
              <RetailerProtectedRoute><ImageSettings /></RetailerProtectedRoute>
            } />
            <Route path="/retailer/settings/visualizer/preview" element={
              <RetailerProtectedRoute><PreviewSettingPage /></RetailerProtectedRoute>
            } />
            <Route path="/retailer/settings/visualizer/catalog" element={
              <RetailerProtectedRoute><CatalogSettingsPage /></RetailerProtectedRoute>
            } />

            <Route path="/selection/:selectionId" element={
              <RetailerProtectedRoute><SelectionPage /></RetailerProtectedRoute>
            } />

            {/* New Quotation Preview & Download Route */}
            <Route path="/quotation-preview/:selectionId" element={
              <RetailerProtectedRoute><QuotationPreviewPage /></RetailerProtectedRoute>
            } />
 
            {/* Admin routes: unrelated auth system, unchanged */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/retailers" element={<AdminRetailers />} />
            <Route path="/admin/retailers/:retailerId/activity" element={<AdminRetailerActivity />} />
 
            {/* Public / visitor-facing routes: unchanged */}
            <Route path="/visualizer" element={<VisualizerRoute />} />
            <Route path="/bulk-upload" element={<BulkProductUpload/>} />
            <Route path="/digital-showroom/:visualizerToken" element={<DigitalShowroom/>} />
            <Route path="/digital-showroom/:visualizerToken/product/:productId" element={<ProductDetail/>} />
            <Route path="/visualizer/:visualizerToken" element={<RetailerVisualizerRoute />} />
          </Routes>
        </div>
      </RetailerAuthProvider>
    </BrowserRouter>
  );
}
